import React, { useEffect, useRef } from "react";
import FileUploadButton from "./FileUploadButton";
import FileRender from "./FileRender";
import { Modal } from "bootstrap";
import ReactDOM from 'react-dom';
import { FileManagerFile } from "./FileManagerFile";
import {useSelector} from "react-redux";
import {RootState} from "../../store";
import {UserRole} from "../../types/User";
import { IMeQrStorageData } from "../MeQrStorageService";
import { Trans, useTranslation } from "react-i18next";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";

interface StylesProps {
    files: FileManagerFile[],
    allowedFileTypes: readonly string[],
    onFileUpload: (file: File) => void;
    onFileDelete: (file: FileManagerFile) => void;
    onFileChoose: (file: FileManagerFile) => void;
    onModalClose: () => void;
    isFullStorage: boolean;
    isUserPremium: boolean;
    fileUploadLoading: boolean;
    meQrStorageData: IMeQrStorageData | null;
}

export const calculateTotalSize = (files: FileManagerFile[]): number => {
    return files.reduce((total, file) => total + file.size, 0);
};

const FileManager: React.FC<StylesProps> = ({
                                                files,
                                                allowedFileTypes,
                                                onFileUpload,
                                                onFileDelete,
                                                onFileChoose,
                                                onModalClose,
                                                isFullStorage,
                                                isUserPremium,
                                                fileUploadLoading,
                                                meQrStorageData
                                            }) => {
    const { t } = useTranslation()
    const lng = useGetUrlLocale()
    const queryString = window.location.search;
    const modalRef = useRef<HTMLDivElement | null>(null);
    const modalRootRef = useRef<HTMLDivElement | null>(null);
    let modalContainerHtml: HTMLDivElement = document.getElementById('file-manager-container') as HTMLDivElement;
    if (!modalContainerHtml) {
        modalContainerHtml = document.createElement('div');
        modalContainerHtml.setAttribute('id', 'file-manager-container');
        document.body.appendChild(modalContainerHtml);
    }
    modalRootRef.current = modalContainerHtml;
    const user = useSelector((state: RootState) => state.appStorage.user);
    const isIframeOnMeQr = useSelector((state: RootState) => state.appStorage.isIframeOnMeQr);
    const isMobileDisplayMode = new URLSearchParams(queryString).get('isMobileDisplayMode') === "true" ? true : null
    const isAdmin = user ? user.roles.includes(UserRole.ROLE_ADMIN) : false;

    useEffect(() => {
        Modal.getOrCreateInstance('#file-manager').show();
        const modalElement = modalRef.current;

        if (modalElement) {
            modalElement.addEventListener('hidden.bs.modal', onModalClose);
        }

        return () => {
            if (modalElement) {
                modalElement.removeEventListener('hidden.bs.modal', onModalClose);
            }
        };
    }, [onModalClose]);

    const privateOnFileChoose = (file: FileManagerFile) => {
        if(!onFileChoose) return;
        
        Modal.getOrCreateInstance('#file-manager').hide();
        onFileChoose(file);
    };

    const formatFileSize = (size: number) => {
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    };

    const totalSizeInMB = calculateTotalSize(files) / (1024 * 1024);
    const isUploadDisabled = totalSizeInMB > 100;
    const isOverLimit = totalSizeInMB > 100;
    const isFullMeQrStorate = meQrStorageData && meQrStorageData.usedStorageSize > meQrStorageData.storageSize;
    const isLoadingMeQrStorage = window.top !== window.self && !meQrStorageData;

    const isButtonDisabled = isMobileDisplayMode !== null
        ? (isUploadDisabled && !isAdmin && !isUserPremium) || fileUploadLoading
        : isFullMeQrStorate || isLoadingMeQrStorage;

    return ReactDOM.createPortal(
        <>
            <div ref={modalRef} className="modal show modal-lg" id="file-manager" tabIndex={-1} role="dialog">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header border-0">
                            <h4 className="modal-title text-dark-gray">{t("settings.File Manager")}</h4>
                            <button type="button" className="btn-close" aria-label="Close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body p-md-4 p-3">
                            <div className="border p-4 rounded-top">
                                <div className='row'>
                                    <div className='col-12 col-md-4 mb-3 mb-md-0'>
                                        <FileUploadButton allowedFileTypes={allowedFileTypes} onSuccess={onFileUpload}
                                        fileUploadLoading={fileUploadLoading || isLoadingMeQrStorage}  disabled={isButtonDisabled}/>
                                    </div>
                                    <div className="col-12 col-md-8">
                                        <div className="bg-secondary-light px-3 h-100 d-flex align-items-center rounded py-2">
                                            <p className='mb-0 text-light-blue fs-7'>
                                                <Trans
                                                  i18nKey="settings.fileInfo"
                                                  components={[<b />]}
                                                />
                                            </p>

                                        </div>
                                    </div>
                                </div>
                                {isFullStorage && <div className="text-danger mt-2">{t("settings.Storage size exceeds")} {meQrStorageData ? formatFileSize(meQrStorageData.storageSize) : "100 MB"}</div>}
                            </div>

                            <div className='p-4 border border-top-0'>
                                <div className='row gy-3 overflow-y-auto file-render-container'>
                                    <FileRender onFileDelete={onFileDelete} onFileChoose={privateOnFileChoose} files={files} />
                                </div>
                            </div>
                            <div className='p-3 border border-top-0'>
                                <div className="border border-warning rounded d-flex align-items-center bg-warning-subtle mb-3 px-2 py-1">
                                    <i className="bi bi-exclamation-circle text-warning me-2"></i>
                                    <p className="text-warning mb-0 fs-7">{t("settings.fileDelitingInfo")}</p>
                                </div>
                                {!isIframeOnMeQr ? (
                                    <>
                                        {!isUserPremium && !isAdmin && (
                                            <div className="bg-main-background position-relative overflow-hidden rounded-4" style={{ height: "8px" }}>
                                                <div
                                                    className={`h-100 ${isOverLimit ? 'bg-danger' : 'bg-blue'} position-absolute translate-middle-y top-50 w-100 rounded-4`}
                                                    style={{ right: totalSizeInMB > 100 ? '0' : `calc(100% - ${totalSizeInMB}%)` }}
                                                ></div>
                                            </div>
                                        )}
                                        <div className="d-flex align-items-center justify-content-between mb-0 mt-2">
                                            <p className="text-background-mobile mb-0 fs-7">{(isUserPremium || isAdmin) ? t("upload.usedStorageOnly", { used: formatFileSize(calculateTotalSize(files)) }) : t("upload.usedStorage", { used: formatFileSize(calculateTotalSize(files)), limitText: "100 MB" })}</p>
                                           {!isUserPremium && <p className="text-background-mobile fs-7 mb-0">{t("settings.Need more space? Upgrade to")}
                                             <a
                                                className="ms-2 text-blue"
                                                href={`${process.env.REACT_APP_BACKEND_ADDRESS}${lng}/pricing/#pricingSection`}
                                              >
                                                {t("settings.Premium")}
                                              </a>
                                            </p>}
                                        </div>
                                    </>
                                ) : (
                                    meQrStorageData && (
                                        <>
                                            <div className="bg-main-background position-relative overflow-hidden rounded-4" style={{ height: "8px" }}>
                                                <div
                                                    className={`h-100 ${meQrStorageData.usedStorageSize * 100 / meQrStorageData.storageSize > 90 ? 'bg-danger' : 'bg-blue'} position-absolute translate-middle-y top-50 w-100 rounded-4`}
                                                    style={{ right: meQrStorageData.usedStorageSize > meQrStorageData.storageSize ? '0' : `calc(100% - ${meQrStorageData.usedStorageSize * 100 / meQrStorageData.storageSize}%)` }}
                                                ></div>
                                            </div>
                                            <div className="d-flex align-items-center justify-content-between mt-2">
                                                <p className="text-background-mobile mb-0 fs-7">
                                                    {t("upload.usedStorage", { used: formatFileSize(meQrStorageData.usedStorageSize), limitText: formatFileSize(meQrStorageData.storageSize)})}
                                                </p>
                                            </div>
                                        </>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>,
        modalRootRef.current
    );
};

export default FileManager;
